import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { PageContainer } from "@/components/ui/page-container";
import { MobilePageTitle } from "@/components/ui/mobile-page-title";
import { Skeleton } from "@/components/ui/skeleton";
import { ResourceError } from "@/components/ui/resource-error";
import { Wizard } from "@/components/ui/wizard";
import type { WizardStep, WizardStepProps } from "@/components/ui/wizard/types";
import { useUnsavedChanges } from "@/hooks/use-unsaved-changes";
import { useAutoCollapsedSidebar } from "@/hooks/use-auto-collapsed-sidebar";
import {
  useAthleteNameBreadcrumb,
  useEntityNameBreadcrumb,
} from "@/hooks/use-breadcrumb/use-breadcrumb-overrides";
import { ConfirmDialog } from "@/components/ui/dialog/confirm-dialog";
import { toast } from "@/components/ui/toast";
import {
  PlanDetailsStep,
  WorkoutStep,
  GroupClassStep,
  ConfirmationStep,
} from "@/components/create-workout-plan/steps";
import { useEditWorkoutPlan } from "@/components/create-workout-plan/use-update-workout-plan";
import { PLAN_TYPE } from "@/constants/workout-plans";
import { PROTECTED_ROUTES } from "@/utils/navigation";
import { getApiErrorMessages } from "@/utils/api-error";
import {
  WorkoutPlanSchema,
  instanceToPlanDetails,
  disciplineInstanceToGroupClassCard,
  workoutPlanExercisesToSegmentCards,
  type WorkoutPlanFormData,
} from "@/schemas/workout-plan";
import { useWorkoutBlockInstances } from "@/queries/use-workout-block-instances";
import { useWorkoutPlan } from "@/queries/use-workout-plans";

// The `$workoutPlanId` param name is shared scaffolding with the rest of this
// route tree, but for a "Discipline" (group class) row it's really the
// WorkoutBlockInstance id, not a WorkoutPlan id — there's no
// single-instance-by-id endpoint, so `workoutBlockId` travels as a search
// param to locate the row via the existing list endpoint.
export const Route = createFileRoute(
  "/(protected)/athletes/$athleteId/workout-plans/$workoutPlanId/edit/",
)({
  component: RouteComponent,
  staticData: { hideAppChromeOnMobile: true },
  validateSearch: (search: Record<string, unknown>) => ({
    workoutBlockId: Number(search.workoutBlockId),
  }),
});

const EMPTY_WORKOUT: WorkoutPlanFormData["workout"] = {
  warmup: [],
  strength_training: [],
  cooldown: [],
};

function RouteComponent() {
  useAutoCollapsedSidebar();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { athleteId, workoutPlanId } = Route.useParams();
  const { workoutBlockId } = Route.useSearch();

  useAthleteNameBreadcrumb(Number(athleteId));

  const {
    data: instances,
    isLoading: instancesLoading,
    error: instancesError,
  } = useWorkoutBlockInstances(workoutBlockId);

  const instance = useMemo(
    () => instances?.find((i) => i.id === Number(workoutPlanId)),
    [instances, workoutPlanId],
  );

  const isWorkoutPlanInstance = instance?.workableType === "WorkoutPlan";
  const {
    data: plan,
    isLoading: planLoading,
    error: planError,
  } = useWorkoutPlan(
    isWorkoutPlanInstance ? instance.workable.id : 0,
    isWorkoutPlanInstance,
  );

  const isLoading = instancesLoading || (isWorkoutPlanInstance && planLoading);
  // Instances finished loading but no row matches workoutPlanId — e.g. the
  // row was deleted between navigation and load. Without this, `values`
  // stays undefined forever and the skeleton never resolves to anything.
  const instanceNotFound = !instancesLoading && !instance;
  const error =
    instancesError ??
    (isWorkoutPlanInstance ? planError : null) ??
    (instanceNotFound ? new Error("Workout block instance not found") : null);

  // Memoized (not recomputed every render like a simpler prefill would be)
  // because the mapping generates fresh `tempId`s per card — an unmemoized
  // object would regenerate them, and therefore remount every exercise/group
  // class card, on every render.
  const values = useMemo(() => {
    if (!instance) return undefined;
    if (instance.workableType === "Discipline") {
      return {
        ...instanceToPlanDetails(instance),
        groupClass: disciplineInstanceToGroupClassCard(instance),
        workout: EMPTY_WORKOUT,
      };
    }
    if (!plan) return undefined;
    return {
      ...instanceToPlanDetails(instance),
      groupClass: [],
      workout: workoutPlanExercisesToSegmentCards(plan.workoutPlanExercises),
    };
    // plan?.updatedAt (not the query object reference) keys the recompute so
    // an incidental background refetch of identical data doesn't regenerate
    // tempIds mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, plan?.updatedAt]);

  const form = useForm<WorkoutPlanFormData>({
    resolver: zodResolver(WorkoutPlanSchema()),
    mode: "onSubmit",
    values,
    defaultValues: {
      name: "",
      weeklyFrequency: 1,
      planType: PLAN_TYPE.Training,
      groupClass: [],
      workout: EMPTY_WORKOUT,
      workoutStepValid: false,
      workoutForceValidate: false,
    },
  });

  const planType = useWatch({ control: form.control, name: "planType" });
  const name = useWatch({ control: form.control, name: "name" });

  useEntityNameBreadcrumb(name);

  const GroupClassStepWithAthlete = useMemo(
    () =>
      function GroupClassStepWithAthlete(
        stepProps: WizardStepProps<WorkoutPlanFormData>,
      ) {
        return <GroupClassStep {...stepProps} athleteId={Number(athleteId)} />;
      },
    [athleteId],
  );

  // groupClass/workout are cleared explicitly by PlanDetailsStep's own
  // confirmation gate (handleSelectPlanType) whenever the user actually picks
  // a different plan type — not by watching `planType` here. A watch-based
  // effect can't tell a genuine user change apart from the async prefill
  // (`values`) landing/settling, and reacting to that distinction via ref
  // timing is fragile under React StrictMode's double effect invocation,
  // which can fire the clear before the prefilled data is ever shown.
  const steps: WizardStep<WorkoutPlanFormData>[] = useMemo(
    () => [
      {
        id: "details",
        label: t("pages:athletes.workoutPlanWizard.steps.planDetails"),
        component: PlanDetailsStep,
        fields: ["name", "weeklyFrequency", "planType"],
      },
      {
        id: "type-step",
        label: t("pages:athletes.workoutPlanWizard.steps.typeDetails"),
        component:
          planType === PLAN_TYPE.GroupClass
            ? GroupClassStepWithAthlete
            : WorkoutStep,
        fields: planType === PLAN_TYPE.GroupClass ? ["groupClass"] : undefined,
        validityField:
          planType === PLAN_TYPE.GroupClass ? undefined : "workoutStepValid",
        onValidationAttempt:
          planType === PLAN_TYPE.GroupClass
            ? undefined
            : () => form.setValue("workoutForceValidate", true),
        nextDisabledField:
          planType === PLAN_TYPE.GroupClass ? undefined : "workoutStepEmpty",
        hideChromeField:
          planType === PLAN_TYPE.GroupClass ? undefined : "workoutEditorOpen",
        disableStepScroll: planType !== PLAN_TYPE.GroupClass,
      },
      {
        id: "confirmation",
        label: t("pages:athletes.workoutPlanWizard.steps.confirmation"),
        component: ConfirmationStep,
      },
    ],
    [t, planType, form, GroupClassStepWithAthlete],
  );

  // Unlike the "new" wizard (which has no single block to return to), this
  // route always knows which block it's editing a row of — send Cancel/Save
  // back to that block's detail view instead of the generic workouts tab.
  const navigateBack = useCallback(
    () =>
      workoutBlockId
        ? navigate({
            to: PROTECTED_ROUTES.ATHLETE_WORKOUT_BLOCK_DETAIL,
            params: { athleteId, workoutBlockId: String(workoutBlockId) },
          })
        : navigate({
            to: PROTECTED_ROUTES.ATHLETE_DETAIL,
            params: { athleteId },
            search: { tab: "workouts" },
          }),
    [navigate, athleteId, workoutBlockId],
  );

  const { handleCancelEdit, unsavedChangesDialogProps } = useUnsavedChanges({
    isEditing: form.formState.isDirty,
    isDirty: form.formState.isDirty,
    onLeave: navigateBack,
  });

  const { submit, isSubmitting } = useEditWorkoutPlan(
    workoutBlockId,
    instance,
    plan,
  );

  const handleComplete = useCallback(
    async (data: WorkoutPlanFormData) => {
      try {
        await submit(data);
        toast.success(
          t("pages:athletes.workoutPlanWizard.toasterEdit.success.title"),
          {
            description: t(
              "pages:athletes.workoutPlanWizard.toasterEdit.success.description",
            ),
          },
        );
        navigateBack();
      } catch (error) {
        toast.error(
          t("pages:athletes.workoutPlanWizard.toasterEdit.error.title"),
          { description: getApiErrorMessages(error as Error) },
        );
      }
    },
    [submit, t, navigateBack],
  );

  const handleCancel = useCallback(
    () => handleCancelEdit(navigateBack),
    [handleCancelEdit, navigateBack],
  );

  return (
    <div className="flex h-full flex-col px-6 py-5">
      <MobilePageTitle>
        {t("pages:athletes.workoutPlanWizard.editTitle")}
      </MobilePageTitle>
      <PageContainer
        className="flex min-h-0 min-w-[309px] flex-1 flex-col px-2 py-0 md:m-0 md:px-0 lg:mb-0"
        internalScroll
      >
        {error ? (
          <ResourceError
            error={error}
            errorMessage={t(
              "pages:profile.workouts.plans.errors.loadingPlanFailed",
            )}
            className="m-6"
          />
        ) : isLoading || !values ? (
          <div className="flex flex-col gap-4 p-8">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <Wizard
            form={form}
            steps={steps}
            onComplete={handleComplete}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            fullWidthStepper
          />
        )}
      </PageContainer>
      <ConfirmDialog {...unsavedChangesDialogProps} />
    </div>
  );
}
